import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Wallet as WalletIcon, TrendingUp, Image as ImageIcon, Percent, Plus, ArrowDownToLine, ArrowUpRight, ArrowDownRight, CreditCard, BarChart3, Landmark, Lock } from "lucide-react";
import { euro, dateTime } from "@/lib/format";
import PaymentMethods from "@/components/PaymentMethods";
import { ConnectSection, connectCanWithdraw } from "@/components/ConnectSection";
import { WithdrawalTracking } from "@/components/WithdrawalTracking";
import { toast } from "sonner";
import { prepareExternal, openExternal, cancelExternal } from "@/lib/external";

const TX_LABELS = {
  recharge: "Recharge",
  purchase: "Achat",
  earning: "Vente — net disponible",
  sale_paid_out: "Vente — net versé sur Stripe",
  earning_reversal: "Vente remboursée — net repris",
  sale_refunded: "Vente remboursée (Stripe)",
  purchase_refund: "Achat remboursé sur votre carte",
  withdrawal: "Retrait",
  refund: "Remboursement",
  fee: "Commission",
};

const SALE_TYPES = ["earning", "sale_paid_out", "earning_reversal", "sale_refunded"];

// Détail d'une vente : brut, commission MIHOD PAY (20 %), net — lisible par le vendeur
const SaleBreakdown = ({ t }) => {
  const m = t.meta || {};
  if (!SALE_TYPES.includes(t.type) || m.gross === undefined) return null;
  const pct = m.fee_percent ?? 20;
  const onStripe = m.settlement && m.settlement !== "platform_ledger";
  return (
    <p className="text-[11px] text-muted-foreground mt-0.5" data-testid={`sale-breakdown-${t.id}`}>
      Prix {euro(m.gross)} · Commission MIHOD PAY {pct} % {m.fee >= 0 ? "−" : "+"}{euro(Math.abs(m.fee))} · Net {euro(m.net)}
      {" · "}
      <span className={onStripe ? "text-primary" : ""}>{onStripe ? "versé directement sur votre compte Stripe" : "crédité sur vos revenus MIHOD"}</span>
    </p>
  );
};

const Wallet = () => {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rechargeOpen, setRechargeOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [amount, setAmount] = useState(20);
  const [wAmount, setWAmount] = useState(0);
  const [busy, setBusy] = useState(false);
  const [payMethod, setPayMethod] = useState("card");
  const [connect, setConnect] = useState(null);
  const [payCfg, setPayCfg] = useState({ live: false, stripe_mode: "test" });
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    api.get("/payments/config").then(({ data }) => setPayCfg(data)).catch(() => {});
  }, []);

  // Retour depuis l'onboarding Stripe Connect (return_url / refresh_url) : on rouvre le
  // panneau de retrait et on informe le vendeur de l'état réel de son compte.
  const connectReturn = searchParams.get("connect");
  useEffect(() => {
    if (!connectReturn) return;
    setWithdrawOpen(true);
    if (connectReturn === "refresh") {
      toast.info("Le lien Stripe a expiré — cliquez sur « Reprendre la vérification Stripe » pour continuer.");
    } else if (connectReturn === "setup") {
      toast.info("Activez vos versements Stripe pour retirer vos revenus sur votre banque.");
    }
    setSearchParams({}, { replace: true });
  }, [connectReturn, setSearchParams]);

  const onConnectStatus = useCallback((st) => {
    setConnect(st);
    if (connectReturn === "return") {
      if (st?.onboarded) toast.success("Compte Stripe connecté — vos retraits en argent réel sont activés.");
      else if (st?.has_account) toast.info(st.requirements_due?.length
        ? "Stripe a encore besoin d'informations : reprenez la vérification pour terminer."
        : "Stripe vérifie vos informations — l'activation prend en général quelques minutes.");
    }
  }, [connectReturn]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/wallet");
      setData(data);
      setWAmount(data.earnings_available);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const topup = async () => {
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt < 1 || amt > 2000) {
      toast.error("Montant invalide. Choisissez entre 1 € et 2000 €.");
      return;
    }
    const reserved = prepareExternal();
    setBusy(true);
    try {
      const { data: res } = await api.post("/wallet/topup", {
        amount: amt,
        origin_url: window.location.origin,
      });
      // Redirection vers le vrai paiement Stripe — le solde sera crédité
      // uniquement après confirmation serveur du paiement.
      if (openExternal(res.checkout_url, reserved) === "tab") {
        toast.info("Le paiement Stripe s'est ouvert dans un nouvel onglet.");
        setBusy(false);
      }
    } catch (e) {
      cancelExternal(reserved);
      toast.error(apiError(e));
      setBusy(false);
    }
  };

  const withdraw = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/wallet/withdraw", { amount: Number(wAmount) });
      setUser(res.user);
      toast.success(res.message);
      setWithdrawOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally { setBusy(false); }
  };

  const kpis = [
    { label: "Solde disponible", value: euro(data?.balance), icon: WalletIcon, testid: "wallet-balance", accent: "text-primary" },
    { label: "Revenus", value: euro(data?.earnings_total), icon: TrendingUp, testid: "wallet-earnings", accent: "text-[hsl(160_84%_32%)]" },
    { label: "Photos vendues", value: data?.photos_sold ?? 0, icon: ImageIcon, testid: "wallet-photos-sold", accent: "text-foreground" },
    { label: "Commission", value: `${data?.platform_fee_percent ?? 20} %`, icon: Percent, testid: "wallet-fee", accent: "text-muted-foreground" },
  ];

  return (
    <div className="space-y-5">
      <h1 className="font-display text-2xl">Portefeuille</h1>

      <div className="grid grid-cols-2 gap-3">
        {kpis.map((k) => {
          const Icon = k.icon;
          return (
            <Card key={k.label} className="p-4 rounded-2xl relative overflow-hidden">
              <div className="absolute -right-4 -top-4 h-16 w-16 rounded-full bg-secondary/60" />
              <Icon className={`h-5 w-5 ${k.accent}`} />
              <p className="text-xs text-muted-foreground mt-3">{k.label}</p>
              <p className="font-display text-xl mt-0.5" data-testid={k.testid}>{loading ? "…" : k.value}</p>
            </Card>
          );
        })}
      </div>

      {!payCfg.deposits_enabled && !loading && (
        <Card className="p-3 rounded-2xl border-[hsl(38_92%_50%)]/40 flex items-start gap-2 text-xs" data-testid="wallet-deposits-blocked">
          <Lock className="h-4 w-4 text-[hsl(38_92%_45%)] shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">Dépôts en attente de la connexion Stripe LIVE</p>
            <p className="text-muted-foreground mt-0.5">Aucun argent fictif n'est accepté. Votre solde ne pourra être crédité qu'après un paiement réel confirmé par Stripe.</p>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-2 gap-3">
        <Button className="rounded-xl h-12" onClick={() => setRechargeOpen(true)} data-testid="wallet-recharge-button">
          <Plus className="h-4 w-4 mr-1" /> Déposer de l'argent
        </Button>
        <Button variant="outline" className="rounded-xl h-12" onClick={() => setWithdrawOpen(true)} data-testid="wallet-withdraw-button">
          <ArrowDownToLine className="h-4 w-4 mr-1" /> Retirer
        </Button>
      </div>

      <Button variant="outline" className="w-full rounded-xl h-12" onClick={() => navigate("/statistiques")} data-testid="wallet-stats-link">
        <BarChart3 className="h-4 w-4 mr-2" /> Statistiques créateur
      </Button>

      {data?.sales_summary?.count > 0 && (
        <Card className="p-4 rounded-2xl" data-testid="sales-summary">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-display text-lg">Mes ventes</h2>
            <span className="text-[11px] text-muted-foreground">{data.sales_summary.count} vente(s)</span>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="rounded-xl bg-secondary/60 p-2">
              <p className="text-[11px] text-muted-foreground">Ventes brutes</p>
              <p className="font-display text-base" data-testid="sales-gross">{euro(data.sales_summary.gross)}</p>
            </div>
            <div className="rounded-xl bg-secondary/60 p-2">
              <p className="text-[11px] text-muted-foreground">Commission MIHOD {data.sales_summary.fee_percent} %</p>
              <p className="font-display text-base text-muted-foreground" data-testid="sales-commission">−{euro(data.sales_summary.commission)}</p>
            </div>
            <div className="rounded-xl bg-[hsla(160,84%,32%,0.12)] p-2">
              <p className="text-[11px] text-muted-foreground">Votre net</p>
              <p className="font-display text-base text-[hsl(160_84%_32%)]" data-testid="sales-net">{euro(data.sales_summary.net)}</p>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground mt-2" data-testid="sales-settlement-note">
            {data.sales_summary.paid_on_stripe > 0 && <>{euro(data.sales_summary.paid_on_stripe)} déjà versés sur votre compte Stripe Connect. </>}
            {data.sales_summary.via_mihod > 0 && <>{euro(data.sales_summary.via_mihod)} crédités sur vos revenus MIHOD (retrait vers Stripe à la demande). </>}
            {data.sales_summary.connect_active
              ? "Compte Stripe actif : vos prochaines ventes vous sont versées automatiquement, la commission de 20 % revient à MIHOD PAY via Stripe Connect."
              : "Activez Stripe Connect pour recevoir automatiquement votre net à chaque vente."}
          </p>
        </Card>
      )}

      <WithdrawalTracking refreshKey={data?.transactions?.length} />

      <div>
        <h2 className="font-display text-lg mb-2">Historique</h2>
        <Card className="rounded-2xl overflow-hidden" data-testid="wallet-ledger-table">
          {(!data || data.transactions.length === 0) ? (
            <p className="text-sm text-muted-foreground p-6 text-center">Aucune transaction pour le moment.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Montant</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.transactions.map((t) => {
                  const positive = t.amount >= 0;
                  return (
                    <TableRow key={t.id}>
                      <TableCell>
                        <span className="flex items-center gap-2">
                          {positive ? <ArrowUpRight className="h-4 w-4 text-[hsl(160_84%_32%)]" /> : <ArrowDownRight className="h-4 w-4 text-muted-foreground" />}
                          {TX_LABELS[t.type] || t.type}
                        </span>
                        <SaleBreakdown t={t} />
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{dateTime(t.created_at)}</TableCell>
                      <TableCell className={`text-right font-medium ${positive ? "text-[hsl(160_84%_32%)]" : ""}`}>
                        {positive ? "+" : ""}{euro(t.amount)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Card>
      </div>

      {/* Recharge dialog */}
      <Dialog open={rechargeOpen} onOpenChange={setRechargeOpen}>
        <DialogContent className="max-w-sm rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">Déposer de l'argent</DialogTitle>
            <DialogDescription>Ajoutez des fonds par un paiement sécurisé. Le solde est crédité uniquement après confirmation du paiement par Stripe.</DialogDescription>
          </DialogHeader>
          {!payCfg.deposits_enabled && (
            <div className="rounded-xl border border-[hsl(38_92%_50%)]/40 bg-[hsla(38,92%,50%,0.08)] p-3 text-xs flex items-start gap-2" data-testid="topup-blocked-notice">
              <Lock className="h-4 w-4 text-[hsl(38_92%_45%)] shrink-0 mt-0.5" />
              <p>{payCfg.blocked_reason || "Dépôt indisponible : connexion Stripe LIVE en attente."}</p>
            </div>
          )}
          <div className="grid grid-cols-4 gap-2">
            {[20, 50, 100, 200].map((v) => (
              <button key={v} onClick={() => setAmount(v)} data-testid={`recharge-preset-${v}`}
                className={`py-2 rounded-xl border text-sm font-medium transition-colors ${Number(amount) === v ? "border-primary bg-primary/5 text-primary" : "border-border hover:border-primary/50"}`}>
                {v}€
              </button>
            ))}
          </div>
          <div>
            <p className="text-xs font-medium mb-1.5">Montant personnalisé (1 – 2000 €)</p>
            <Input type="number" min={1} max={2000} value={amount} onChange={(e) => setAmount(e.target.value)} className="rounded-xl" data-testid="recharge-amount-input" placeholder="Ex : 75" />
          </div>
          <p className="text-xs font-medium">Moyen de paiement</p>
          <PaymentMethods value={payMethod} onChange={setPayMethod} />
          <DialogFooter className="flex-col sm:flex-col gap-2">
            <Button onClick={topup} disabled={busy || !payCfg.deposits_enabled} className="w-full rounded-xl neon-glow-blue-sm" data-testid="recharge-confirm">
              <CreditCard className="h-4 w-4 mr-1" /> {payCfg.deposits_enabled ? `Déposer ${euro(amount)}` : "Dépôt indisponible"}
            </Button>
            <p className="text-[11px] text-center text-muted-foreground" data-testid="topup-mode-hint">{payCfg.live ? "Paiement réel sécurisé via Stripe — Cartes, Apple\u00a0Pay et Google\u00a0Pay." : "Le dépôt sera disponible dès la connexion de Stripe LIVE. Aucun montant fictif n'est jamais crédité."}</p>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Withdraw dialog */}
      <Dialog open={withdrawOpen} onOpenChange={setWithdrawOpen}>
        <DialogContent className="max-w-sm rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">Retirer mes revenus</DialogTitle>
            <DialogDescription>Revenus disponibles : <strong>{euro(data?.earnings_available)}</strong>. Retrait minimum 15 € — versé sur votre compte Stripe puis sur votre banque (2 à 7 jours). Aucun argent fictif.</DialogDescription>
          </DialogHeader>
          <Input type="number" min={0} max={data?.earnings_available || 0} value={wAmount} onChange={(e) => setWAmount(e.target.value)} className="rounded-xl" data-testid="withdraw-amount-input" />
          <div className="grid grid-cols-4 gap-2">
            {[20, 50, 200, 500].map((v) => (
              <button key={v} onClick={() => setWAmount(v)} data-testid={`withdraw-preset-${v}`}
                className={`py-2 rounded-xl border text-sm font-medium transition-colors ${Number(wAmount) === v ? "border-primary bg-primary/5" : "border-border"}`}>
                {v}€
              </button>
            ))}
          </div>

          {/* Withdrawal destinations */}
          <div className="rounded-xl bg-secondary/50 p-3 space-y-1.5" data-testid="withdraw-methods">
            <p className="text-[11px] font-medium text-muted-foreground">Destinations de retrait</p>
            <div className="flex flex-wrap gap-1.5">
              <span className="flex items-center gap-1 rounded-lg border border-border bg-card px-2 py-1 text-[11px] font-medium"><Landmark className="h-3.5 w-3.5 text-primary" /> Virement bancaire (IBAN)</span>
              <span className="flex items-center gap-1 rounded-lg border border-border bg-card px-2 py-1 text-[11px] font-medium"><CreditCard className="h-3.5 w-3.5 text-primary" /> Carte Visa / Mastercard</span>
            </div>
            <p className="text-[11px] text-muted-foreground">Bientôt : PayPal, MIA (Moldavie), Mir (Russie), Pix (Brésil).</p>
          </div>

          {/* Stripe Connect : compte vendeur, identité, IBAN */}
          <ConnectSection earningsAvailable={data?.earnings_available || 0} onReady={onConnectStatus} />

          <DialogFooter>
            <Button variant="ghost" onClick={() => setWithdrawOpen(false)}>Annuler</Button>
            <Button onClick={withdraw} disabled={busy || !connectCanWithdraw(connect, data?.earnings_available) || Number(wAmount) < (connect?.min_withdraw ?? 15) || Number(wAmount) > Number(data?.earnings_available || 0)} data-testid="withdraw-confirm">Retirer {euro(wAmount)}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Wallet;
